import { ButtonV2, InputV2 } from '@cimplico/components';
import { CalendarIcon, CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useCallback, useEffect, useRef } from 'react';
import { FieldType } from '../../../../types/custom-worksheet-v2';
import FieldTypeSelect from '../../../../components/field-type-select/FieldTypeSelect';

type EditInputProps = {
    allowedTypes?: FieldType[];
    inputType?: 'string' | 'type' | 'date';
    showActionButtons?: boolean;
    autoFocus?: boolean;
    value?: string | FieldType;
    placeholder?: string;
    errorMessage?: string | null;
    characterLimit?: number | undefined;
    onChange: (value?: string | FieldType) => void;
    onSave?: () => void;
    onCancel?: () => void;
};

function EditInput({
    allowedTypes,
    inputType = 'string',
    showActionButtons = true,
    autoFocus = true,
    value,
    placeholder,
    errorMessage,
    characterLimit,
    onChange,
    onSave,
    onCancel,
}: EditInputProps): JSX.Element {
    const outerDivRef = useRef<HTMLDivElement | null>(null);

    /**
     * Revert changes on input value on 'Escape' key clicked
     * Save changes on input value on 'Enter' key clicked
     */
    const handleKeyboardShortcut = useCallback(
        function handleKeyboardShortcut(event: KeyboardEvent): void {
            if (onCancel && event.key === 'Escape') {
                onCancel();
            } else if (onSave && event.key === 'Enter') {
                onSave();
            }
        },
        [onSave, onCancel]
    );

    /**
     * When user clicks outside of the input area, blurs the input and save updated value
     */
    const handleClickOutside = useCallback(
        function handleClickOutside(event: MouseEvent): void {
            if (!(event.target instanceof Element) || !onSave) {
                return;
            }

            // If click event is within outerDivRef area that wraps input and action buttons, don't clear edit layout
            if (outerDivRef?.current && (outerDivRef.current.contains(event.target) || outerDivRef.current.isEqualNode(event.target))) {
                return;
            }

            // If click event is out of outerDivRef area that wraps input and action buttons
            if (outerDivRef?.current && !outerDivRef.current.contains(event.target)) {
                onSave();
            }
        },
        [onSave]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return (): void => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyboardShortcut);
        return (): void => document.removeEventListener('keydown', handleKeyboardShortcut);
    }, [handleKeyboardShortcut]);

    return (
        <div ref={outerDivRef} className="flex items-start relative w-full">
            <div className="grow">
                {errorMessage && <p className="text-xs text-red-600 absolute top-9">{errorMessage}</p>}
                {inputType === 'string' && (
                    <InputV2
                        isError={Boolean(errorMessage)}
                        showCharacterLimitHint={false}
                        characterLimit={characterLimit}
                        autoFocus={autoFocus}
                        placeholder={placeholder}
                        value={value || ''}
                        type="text"
                        onChange={onChange}
                    />
                )}
                {inputType === 'date' && (
                    <InputV2
                        isError={Boolean(errorMessage)}
                        autoFocus={autoFocus}
                        placeholder={placeholder}
                        value={value || ''}
                        leftIcon={<CalendarIcon />}
                        type="date"
                        onChange={onChange}
                    />
                )}
                {inputType === 'type' && (
                    <FieldTypeSelect
                        allowedTypes={
                            allowedTypes || [
                                FieldType.ACCOUNTING,
                                FieldType.DATE,
                                FieldType.MONEY,
                                FieldType.NUMBER,
                                FieldType.PERCENT,
                                FieldType.TEXT,
                            ]
                        }
                        autoFocus={autoFocus}
                        value={value ? (value as FieldType) : undefined}
                        onChange={(val): void => onChange(val.length === 0 ? undefined : val[0])}
                    />
                )}
            </div>
            {showActionButtons && (
                <div className="absolute left-full flex gap-1 items-center ml-1 z-50">
                    <ButtonV2 type="secondary" leftIcon={<CheckIcon className="text-green-500" />} onClick={onSave} />
                    <ButtonV2 type="secondary" leftIcon={<XMarkIcon className="text-red-500" />} onClick={onCancel} />
                </div>
            )}
        </div>
    );
}

export default EditInput;
